/**
 * Ligações do browser ao equipamento (Chrome/Edge): Web Serial, WebUSB e Web
 * Bluetooth. Os tipos não vêm no lib DOM do TypeScript — só o que se usa.
 * O utilizador autoriza cada aparelho uma vez (lista do browser); a porta
 * série e o USB voltam a ligar-se sozinhos nas sessões seguintes.
 */

export interface SerialPortLike {
  open(o: { baudRate: number }): Promise<void>;
  close(): Promise<void>;
  readonly readable: ReadableStream<Uint8Array> | null;
  readonly writable: WritableStream<Uint8Array> | null;
  getInfo(): { usbVendorId?: number; usbProductId?: number };
}
interface UsbEndpoint { endpointNumber: number; direction: 'in' | 'out'; type: 'bulk' | 'interrupt' | 'isochronous' }
interface UsbAlternate { interfaceClass: number; endpoints: UsbEndpoint[] }
interface UsbInterface { interfaceNumber: number; alternate: UsbAlternate }
export interface UsbDeviceLike {
  vendorId: number; productId: number; serialNumber?: string; productName?: string; opened: boolean;
  configuration: { interfaces: UsbInterface[] } | null;
  open(): Promise<void>; close(): Promise<void>; selectConfiguration(n: number): Promise<void>; claimInterface(n: number): Promise<void>;
  transferOut(endpoint: number, data: Uint8Array): Promise<{ status: string }>;
}
interface GattCharacteristic { properties: { write: boolean; writeWithoutResponse: boolean }; writeValueWithoutResponse(d: Uint8Array): Promise<void>; writeValueWithResponse(d: Uint8Array): Promise<void> }
interface GattService { getCharacteristics(): Promise<GattCharacteristic[]> }
export interface BtDeviceLike { id: string; name?: string; gatt?: { connected: boolean; connect(): Promise<{ getPrimaryServices(): Promise<GattService[]> }> } }

type Nav = Navigator & {
  serial?: { requestPort(o?: object): Promise<SerialPortLike>; getPorts(): Promise<SerialPortLike[]> };
  usb?: { requestDevice(o: object): Promise<UsbDeviceLike>; getDevices(): Promise<UsbDeviceLike[]> };
  bluetooth?: { requestDevice(o: object): Promise<BtDeviceLike>; getDevices?(): Promise<BtDeviceLike[]> };
};
const nav = () => navigator as Nav;

/**
 * Ponte da aplicação Android (apps/pos-android): impressora embutida Sunmi,
 * Bluetooth clássico, USB e balanças USB-série. O lado nativo (MakilaAndroid,
 * um @JavascriptInterface) responde por callback em window.__makilaCb, para
 * não bloquear a página enquanto imprime.
 */
export interface NativeDevice { id: string; kind: 'printer' | 'scale'; label: string }
export interface NativeBridge {
  devices(): NativeDevice[];
  print(target: string, base64: string): Promise<void>;
  readScale(target: string, protocol: string, baudRate: number): Promise<string>;
}
interface AndroidRaw { version(): string; devices(): string; print(cb: string, target: string, base64: string): void; readScale(cb: string, target: string, protocol: string, baud: number): void }
const pending = new Map<string, { ok: (v: string) => void; fail: (e: Error) => void }>();
(globalThis as { __makilaCb?: unknown }).__makilaCb = (id: string, err: string | null, result: string | null) => {
  const p = pending.get(id); if (!p) return;
  pending.delete(id);
  if (err) p.fail(new Error(err)); else p.ok(result ?? '');
};
let seq = 0;
const nativeCall = (fn: (cb: string) => void, timeoutMs: number) => new Promise<string>((ok, fail) => {
  const id = `c${++seq}`;
  pending.set(id, { ok, fail });
  setTimeout(() => { if (pending.delete(id)) fail(new Error('O terminal não respondeu')); }, timeoutMs);
  fn(id);
});
export const nativeBridge = (): NativeBridge | undefined => {
  const a = (globalThis as { MakilaAndroid?: AndroidRaw }).MakilaAndroid;
  if (!a) return undefined;
  return {
    devices: () => { try { return JSON.parse(a.devices()) as NativeDevice[]; } catch { return []; } },
    print: async (target, b64) => { await nativeCall((cb) => a.print(cb, target, b64), 60_000); },
    readScale: (target, protocol, baud) => nativeCall((cb) => a.readScale(cb, target, protocol, baud), 8_000),
  };
};

export const support = () => ({ serial: !!nav().serial, usb: !!nav().usb, bluetooth: !!nav().bluetooth, native: !!nativeBridge() });

/** Serviços BLE das impressoras térmicas mais vendidas (Xprinter, MTP, Goojprt, "Printer-xxx"). */
const BLE_SERVICES = ['000018f0-0000-1000-8000-00805f9b34fb', 'e7810a71-73ae-499d-8c15-faa9aef0c3f2', '49535343-fe7d-4ae5-8fa9-9fafd205e455', '0000ff00-0000-1000-8000-00805f9b34fb', '0000ffe0-0000-1000-8000-00805f9b34fb'];

/** O que fica guardado para voltar a encontrar o aparelho (sem o abrir). */
export interface DeviceRef { vendorId?: number; productId?: number; serialNumber?: string; btId?: string; nativeId?: string; label: string }

export const pickSerial = async (): Promise<{ port: SerialPortLike; ref: DeviceRef }> => {
  const port = await nav().serial!.requestPort();
  const i = port.getInfo();
  return { port, ref: { vendorId: i.usbVendorId, productId: i.usbProductId, label: i.usbVendorId ? `Porta série USB ${hex(i.usbVendorId)}:${hex(i.usbProductId ?? 0)}` : 'Porta série' } };
};
export const findSerial = async (ref: DeviceRef): Promise<SerialPortLike | undefined> => {
  const ports = await nav().serial?.getPorts() ?? [];
  return ports.find((p) => { const i = p.getInfo(); return i.usbVendorId === ref.vendorId && i.usbProductId === ref.productId; }) ?? (ports.length === 1 ? ports[0] : undefined);
};

export const pickUsb = async (): Promise<{ dev: UsbDeviceLike; ref: DeviceRef }> => {
  // Classe 7 = impressora; muitas térmicas declaram "específica do fabricante": pede-se sem filtro de classe.
  const dev = await nav().usb!.requestDevice({ filters: [] });
  return { dev, ref: { vendorId: dev.vendorId, productId: dev.productId, serialNumber: dev.serialNumber, label: dev.productName || `USB ${hex(dev.vendorId)}:${hex(dev.productId)}` } };
};
export const findUsb = async (ref: DeviceRef): Promise<UsbDeviceLike | undefined> =>
  (await nav().usb?.getDevices() ?? []).find((d) => d.vendorId === ref.vendorId && d.productId === ref.productId && (!ref.serialNumber || d.serialNumber === ref.serialNumber));

export const pickBluetooth = async (): Promise<{ dev: BtDeviceLike; ref: DeviceRef }> => {
  const dev = await nav().bluetooth!.requestDevice({ acceptAllDevices: true, optionalServices: BLE_SERVICES });
  return { dev, ref: { btId: dev.id, label: dev.name || 'Impressora Bluetooth' } };
};
export const findBluetooth = async (ref: DeviceRef): Promise<BtDeviceLike | undefined> =>
  (await nav().bluetooth?.getDevices?.() ?? []).find((d) => d.id === ref.btId);

const hex = (n: number) => n.toString(16).padStart(4, '0');

// ── Escrita ──

export async function writeSerial(port: SerialPortLike, bytes: Uint8Array, baudRate: number) {
  if (!port.writable) await port.open({ baudRate });
  const w = port.writable!.getWriter();
  try { await w.write(bytes); } finally { w.releaseLock(); }
}

export async function writeUsb(dev: UsbDeviceLike, bytes: Uint8Array) {
  if (!dev.opened) await dev.open();
  if (!dev.configuration) await dev.selectConfiguration(1);
  const ifs = dev.configuration!.interfaces;
  // A interface de impressora (classe 7) com saída bulk; senão, a primeira com saída bulk.
  const pick = ifs.find((i) => i.alternate.interfaceClass === 7 && i.alternate.endpoints.some(isOut)) ?? ifs.find((i) => i.alternate.endpoints.some(isOut));
  if (!pick) throw new Error('Este aparelho USB não tem saída de impressão');
  try { await dev.claimInterface(pick.interfaceNumber); } catch { /* já reclamada nesta sessão */ }
  const ep = pick.alternate.endpoints.find(isOut)!.endpointNumber;
  for (let o = 0; o < bytes.length; o += 16_384) {
    const r = await dev.transferOut(ep, bytes.subarray(o, o + 16_384));
    if (r.status !== 'ok') throw new Error(`USB: ${r.status}`);
  }
}
const isOut = (e: UsbEndpoint) => e.direction === 'out' && e.type === 'bulk';

export async function writeBluetooth(dev: BtDeviceLike, bytes: Uint8Array) {
  const server = await dev.gatt!.connect();
  let ch: GattCharacteristic | undefined;
  for (const s of await server.getPrimaryServices()) {
    ch = (await s.getCharacteristics()).find((c) => c.properties.writeWithoutResponse || c.properties.write);
    if (ch) break;
  }
  if (!ch) throw new Error('Esta impressora Bluetooth não aceita escrita');
  // Pedaços pequenos (o MTU do BLE); sem resposta quando se pode — muito mais rápido.
  let size = 180;
  for (let o = 0; o < bytes.length;) {
    const part = bytes.subarray(o, o + size);
    try {
      if (ch.properties.writeWithoutResponse) await ch.writeValueWithoutResponse(part); else await ch.writeValueWithResponse(part);
      o += part.length;
    } catch (e) {
      if (size === 20) throw e;
      size = 20; // MTU mínimo: tenta outra vez mais devagar
    }
  }
}

export const toBase64 = (b: Uint8Array) => { let s = ''; for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000)); return btoa(s); };
